<template>
    <div id="conversation-messages" class="conversation-messages">
        <conversation-alert :key="interlocutor().id"/>

        <conversation-messages-suggestion-box v-if="showConversationMessagesSuggestionBox"/>

        <div id="conversation-messages-list-container" :class="containerClass">
            <template v-for="date in conversationMessagesDates">
                <div v-if="date !== sendingStatus" class="message-date">
                    <span>{{ formatDate(date) }}</span>
                </div>

                <div id="conversation-messages-list">
                    <conversation-messages-tooltip/>

                    <chat-bot/>

                    <conversation-message v-if="isTyping(getSelectedParticipants)" :message="isTypingMessage"/>

                    <conversation-message v-for="message in getMessagesByDate(date)"
                                          :message="message"
                                          :key="message.id + message.created_at"/>
                </div>
            </template>
        </div>

        <conversation-hearts v-if="canSendHearts"/>

        <conversation-sticker-animations/>
    </div>
</template>

<style lang="scss">
.conversation-messages {
    @apply flex-1 flex relative overflow-hidden;
}

.conversation-messages-list-container {
    @apply flex-1 overflow-y-scroll bg-neutral-100;

    &--background {
        @apply bg-conversation-background-image;
    }
}

#conversation-messages-list {
    @apply flex flex-col-reverse overflow-hidden px-0 md:px-3 lg:px-6 xl:px-9 2xl:px-12 pb-3;
}

.message-date {
    @apply sticky top-4 z-1 flex justify-center mt-4 mb-2;

    span {
        @apply bg-white text-sm text-dark text-center rounded-lg leading-normal min-w-32 border border-b-2 border-neutral-300;
    }
}
</style>

<script>
import BaseAlert from "../elements/BaseAlert";
import BaseButton from "../elements/BaseButton";
import ChatBot from "./ChatBot";
import ChatMixin from "./ChatMixin";
import ConversationAlert from "./ConversationAlert";
import ConversationHearts from "./ConversationHearts.vue";
import ConversationMessage from "./ConversationMessage";
import ConversationMessagesSuggestionBox from "./ConversationMessagesSuggestionBox";
import ConversationMessagesTooltip from "./ConversationMessagesTooltip";
import ConversationStickerAnimations from "./ConversationStickerAnimations.vue";
import {mapActions, mapGetters} from "vuex";
import moment from "moment-timezone";

export default {
    name: "ConversationMessages",
    components: {
        ConversationStickerAnimations,
        ConversationHearts,
        ConversationMessagesSuggestionBox,
        ConversationMessagesTooltip,
        ConversationAlert,
        BaseAlert,
        BaseButton,
        ConversationMessage,
        ChatBot,
    },
    mixins: [ChatMixin],
    computed: {
        ...mapGetters("chat", [
            "canSendHearts",
            "getChatBot",
            "getChatBotMastersAsked",
            "getConversationMessages",
            "getHoursBeforeAskingEnabledAgain",
            "getSelectedParticipants",
            "hasChatBotReasonToShow",
            "hasLiveSessionBillingStarted",
            "iAmMaster",
            "isChatBotShown",
            "isChatSessionAlmostOver",
            "isConversationsLoading",
            "isLiveConversationInProgress",
            "isLiveConversationWaiting",
            "isTyping",
            "isTypingType",
            "refillFailed",
            "refillSuccess",
        ]),
        chatBotMessage: function () {
            return {
                content: {},
                type: "chat_bot_ask_question",
                error: false,
                participant: this.interlocutor(),
            };
        },
        conversationsMessages() {
            return this.getConversationMessages(this.getSelectedParticipants).slice().reverse();
        },
        conversationMessagesDates() {
            const messagesByDate = this.conversationsMessagesByDate;
            return Object.keys(messagesByDate).sort();
        },
        conversationsMessagesByDate() {
            return this.conversationsMessages.reduce((messagesByDate, message) => {
                const dateMoment = this.getDateMomentInUserLocale(message.created_at);
                const key = dateMoment.isValid() ? dateMoment.format("YYYYMMDD") : this.sendingStatus;

                if (!messagesByDate[key]) {
                    messagesByDate[key] = [];
                }

                messagesByDate[key].push(message);
                return messagesByDate;
            }, {});
        },
        containerClass() {
            return {
                "conversation-messages-list-container": true,
                "conversation-messages-list-container--background": this.iAmMaster || this.conversationsMessages.length > 0,
            };
        },
        showConversationMessagesSuggestionBox() {
            return !this.isLiveConversationWaiting && !this.isLiveConversationInProgress && this.conversationsMessages.length === 0;
        },
        isTypingMessage() {
            return {
                participant: this.interlocutor(),
                type: 'text',
                content: {text: this.isTypingText}
            }
        },
        isTypingText() {
            let name = this.interlocutor(this.getSelectedParticipants).name;

            if (this.isTypingType(this.getSelectedParticipants) === "audio") {
                return this.trans.get("chat.is_typing_audio", {name});
            }

            return this.trans.get("chat.is_typing_text", {name});
        },
        sendingStatus() {
            return "sending";
        },
    },
    methods: {
        ...mapActions("chat", ["askChatBotQuestion"]),
        getMessagesByDate(date) {
            const conversationsMessagesByDate = this.conversationsMessagesByDate;
            return conversationsMessagesByDate[date];
        },
        getDateMomentInUserLocale(date) {
            const dbTimeZone = "Europe/Paris";
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            if (date === null) {
                return moment().tz(userTimeZone);
            }

            return moment.tz(date, dbTimeZone).tz(userTimeZone);
        },
        formatDate(date) {
            const momentDate = this.getDateMomentInUserLocale(date);
            const now = this.getDateMomentInUserLocale(null);

            if (momentDate.isSame(now, "day")) {
                return this.trans.get("chat.today");
            }

            if (momentDate.isSame(now.clone().subtract(1, "day"), "day")) {
                return this.trans.get("chat.yesterday");
            }

            return momentDate.format("ddd DD MMM");
        },
        mounted() {
            const conversationMessages = document.getElementById("conversation-messages");
            new ResizeObserver(this.scrollMessagesToBottom).observe(conversationMessages);
        },
        updated() {
            this.scrollMessagesToBottom();
        },
    },
};
</script>
